import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuthProfile } from "@/hooks/use-auth-profile";
import { useToast } from "@/hooks/use-toast";
import { acceptRecipeBoxInvitation, declineRecipeBoxInvitation } from "@/lib/recipe-box-invitations-service";
import { FirebaseError } from "firebase/app";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const STORAGE_KEY = "ezrecipebox.pendingInviteToken";

const truthy = new Set(["1", "true", "yes", "accept", "accepted"]);
const falsy = new Set(["0", "false", "no", "decline", "declined"]);

type RequestedAction = "accept" | "decline" | null;

type InvitationStatus = "idle" | "accepted" | "declined";

const buildRedirectUrl = (basePath: string, token: string | null): string => {
  if (!token) {
    return basePath;
  }

  const redirectTarget = `/invite?token=${encodeURIComponent(token)}`;
  const separator = basePath.includes("?") ? "&" : "?";
  return `${basePath}${separator}redirect=${encodeURIComponent(redirectTarget)}`;
};

const getInviteErrorMessage = (error: unknown): string => {
  if (error instanceof FirebaseError) {
    switch (error.code) {
      case "functions/permission-denied":
        return "Sign in with the email address that received this invitation.";
      case "functions/failed-precondition":
        return "This invitation is no longer valid.";
      case "functions/not-found":
        return "We couldn't find this invitation.";
      case "functions/unauthenticated":
        return "Sign in to respond to this invitation.";
      default:
        return "Unable to update the invitation right now.";
    }
  }

  return "Something went wrong. Please try again.";
};

export default function InvitePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuthProfile();
  const { toast } = useToast();

  const rawToken = searchParams.get("token");
  const rawAction = searchParams.get("accept") ?? searchParams.get("action");

  const requestedAction: RequestedAction = useMemo(() => {
    if (!rawAction) {
      return null;
    }
    const normalized = rawAction.toLowerCase();
    if (truthy.has(normalized)) {
      return "accept";
    }
    if (falsy.has(normalized)) {
      return "decline";
    }
    return null;
  }, [rawAction]);

  const [resolvedToken, setResolvedToken] = useState<string | null>(null);
  const [status, setStatus] = useState<InvitationStatus>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isResponding, setIsResponding] = useState(false);
  const autoActionTriggered = useRef(false);

  useEffect(() => {
    const windowRef = typeof window === "undefined" ? null : window;
    if (!windowRef) {
      return;
    }

    if (rawToken && rawToken.trim()) {
      const trimmed = rawToken.trim();
      windowRef.sessionStorage.setItem(STORAGE_KEY, trimmed);
      setResolvedToken(trimmed);
      return;
    }

    const stored = windowRef.sessionStorage.getItem(STORAGE_KEY);
    setResolvedToken(stored && stored.trim() ? stored.trim() : null);
  }, [rawToken]);

  useEffect(() => {
    if (!resolvedToken) {
      autoActionTriggered.current = false;
    }
  }, [resolvedToken]);

  const handleRespond = useCallback(
    async (accept: boolean) => {
      if (!resolvedToken) {
        setErrorMessage("This invitation link is missing a token.");
        return;
      }

      if (!user) {
        setErrorMessage("Sign in to respond to this invitation.");
        return;
      }

      setIsResponding(true);
      setErrorMessage(null);

      try {
        const result = accept
          ? await acceptRecipeBoxInvitation(resolvedToken)
          : await declineRecipeBoxInvitation(resolvedToken);

        const newStatus: InvitationStatus = accept ? "accepted" : "declined";
        setStatus(newStatus);

        const windowRef = typeof window === "undefined" ? null : window;
        if (windowRef) {
          windowRef.sessionStorage.removeItem(STORAGE_KEY);
        }

        if (result.action === "accepted" && "boxId" in result) {
          toast({
            title: "Invitation accepted",
            description: "Redirecting to your new recipe box…",
          });
          navigate(`/recipe-box/${result.boxId}`);
          return;
        }

        toast({
          title: "Invitation declined",
          description: "You can accept a new invitation anytime.",
        });
        navigate("/home");
      } catch (error) {
        console.error("Failed to respond to invitation", error);
        setErrorMessage(getInviteErrorMessage(error));
      } finally {
        setIsResponding(false);
      }
    },
    [resolvedToken, navigate, toast, user],
  );

  useEffect(() => {
    if (!requestedAction || !resolvedToken || !user || status !== "idle" || isResponding) {
      return;
    }

    if (autoActionTriggered.current) {
      return;
    }

    autoActionTriggered.current = true;
    void handleRespond(requestedAction === "accept");
  }, [requestedAction, resolvedToken, user, status, isResponding, handleRespond]);

  if (!resolvedToken && !authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>Invitation not found</CardTitle>
            <CardDescription>We couldn&apos;t find an invitation token in this link.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button asChild>
              <Link to="/home">Go to dashboard</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle>Recipe box invitation</CardTitle>
          <CardDescription>
            {user
              ? "Accept to add this recipe box to your account."
              : "Sign in or create an account to respond."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {errorMessage ? (
            <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {errorMessage}
            </p>
          ) : null}

          {authLoading ? (
            <p className="text-sm text-muted-foreground">Checking your account…</p>
          ) : null}

          {!user && !authLoading ? (
            <div className="space-y-3">
              <Button asChild className="w-full">
                <Link to={buildRedirectUrl("/login", resolvedToken)}>
                  Sign in to accept
                </Link>
              </Button>
              <Button asChild variant="outline" className="w-full">
                <Link to={buildRedirectUrl("/signup", resolvedToken)}>
                  Create an account
                </Link>
              </Button>
            </div>
          ) : null}

          {user ? (
            <div className="space-y-3">
              <Button className="w-full" onClick={() => handleRespond(true)} disabled={isResponding}>
                {isResponding && status !== "declined" ? "Accepting…" : "Accept invitation"}
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => handleRespond(false)}
                disabled={isResponding}
              >
                {isResponding && status === "declined" ? "Declining…" : "Decline invitation"}
              </Button>
            </div>
          ) : null}

          {status === "accepted" && user ? (
            <p className="text-sm text-muted-foreground">Adding the recipe box to your dashboard…</p>
          ) : null}

          {status === "declined" && user ? (
            <p className="text-sm text-muted-foreground">Invitation declined. Redirecting to your dashboard…</p>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}

